import React, { FunctionComponent, useState } from 'react'
import { ChildParent } from '@/types/Child'
import { TableBody, TableCell, TableRow } from '@mui/material'
import { Role } from '@/types/Role'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import styles from './adultTable.module.scss'
import { Close, Person } from '@mui/icons-material'
import { useAuth } from '@/modules/context/AuthContext'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { UPDATE_PARENT_LINK } from '@/modules/API/parent/parentHandler'
import { useTranslation } from 'react-i18next'
import Desktop from '@/modules/desktopMobile/desktop'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AdultTableRowProps {
    parent: ChildParent
    daycareId: number
    childId: number
    handleRefetchChild: () => void
}

const AdultTableRow: FunctionComponent<AdultTableRowProps> = ({
    parent,
    childId,
    daycareId,
    handleRefetchChild
}: AdultTableRowProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [request, setRequest] = useState<boolean>(parent.request)

    function handleRequestSlot(): void {
        setRequest(!request)
        updateParentLink()
    }

    const [updateParentLink] = useMutation(UPDATE_PARENT_LINK, {
        variables: {
            child_id: childId,
            parent_id: parent.parentId,
            daycare_id: daycareId,
            request: !request
        },
        onCompleted: (): void => {
            showNotificationSuccess(t('parentLinkUpdated'))
            handleRefetchChild()
        },
        onError: (): void => {
            showNotificationError(t('errorParentLinkUpdate'))
        }
    })

    return (
        <TableBody>
            <TableRow>
                <Desktop>
                    <TableCell>
                        <div className={styles.IconContainer}>
                            <Person
                                onClick={() => navigate('/adults?parent=' + parent.parentId)}
                                className={styles.Icon}
                            />
                        </div>
                    </TableCell>
                    <TableCell>{parent.firstname}</TableCell>
                </Desktop>
                <TableCell
                    onClick={() => navigate('/adults?parent=' + parent.parentId)}
                    className={styles.CellClickable}
                >
                    {parent.lastname}
                </TableCell>
                <Desktop>
                    <TableCell>
                        {parent.email ? (
                            <a href={`mailto:${parent.email}`}>{parent.email}</a>
                        ) : (
                            <Close className={styles.RedIcon} />
                        )}
                    </TableCell>
                    <TableCell>
                        {parent.phone ? (
                            <a href={`tel:${parent.phone}`}>{parent.phone}</a>
                        ) : (
                            <Close className={styles.RedIcon} />
                        )}
                    </TableCell>
                </Desktop>
                {user?.currentDaycareRights.canReadOccasionalSlot && user.role !== Role.parent && (
                    <TableCell align={'center'}>
                        <div className={styles.IconContainer}>
                            <Checkbox
                                checked={request}
                                disabled={!user?.currentDaycareRights.canWriteOccasionalSlot}
                                onChange={handleRequestSlot}
                                className={styles.Checkbox}
                            />
                        </div>
                    </TableCell>
                )}
                <TableCell align={'center'}>
                    <a
                        href={'/adults?parent=' + parent.parentId}
                        onClick={(e: React.MouseEvent): void => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate('/adults?parent=' + parent.parentId)
                            }
                        }}
                        className={styles.IconContainer}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </TableCell>
            </TableRow>
        </TableBody>
    )
}

export default AdultTableRow
